/* eslint-disable no-mixed-operators */
import {TimeSpan, UnixTime} from './time';
import {Observable, Subject} from 'rxjs';
import {SkipList} from './skipList/skipList';
import {createDispatchQueue} from './promiseQueue';
import {SkipListNode} from './skipList/skipListNode';
import {RecordSeries} from './data';
import {isUndefined, Maybe} from './utils/maybe';
import {isNumber} from './utils/runtimeTypeChecking';
import {I_CsvEntry} from 'src/content/dashboards/Log/graph.util';
import {I_S3Credentials} from '../interfaces/S3Types';

export const FetchResult = {
  notAvailable: 'N/A',
  tryLater: 'Try Later'
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type FetchResult<T> =
  | T
  | typeof FetchResult.notAvailable
  | typeof FetchResult.tryLater;

function reverseBits(x: number): number {
  // https://stackoverflow.com/a/60227327/141397

  x = ((x & 0x55555555) << 1) | ((x & 0xaaaaaaaa) >> 1);
  x = ((x & 0x33333333) << 2) | ((x & 0xcccccccc) >> 2);
  x = ((x & 0x0f0f0f0f) << 4) | ((x & 0xf0f0f0f0) >> 4);
  x = ((x & 0x00ff00ff) << 8) | ((x & 0xff00ff00) >> 8);
  x = ((x & 0x0000ffff) << 16) | ((x & 0xffff0000) >> 16);

  return x >>> 0;
}

export default class DataCache<T extends Record<string, I_CsvEntry>> {
  readonly _fetch: (
    t: UnixTime,
    s3Credentials: I_S3Credentials
  ) => Promise<FetchResult<T>>;

  public readonly gotData: Observable<UnixTime>;

  private readonly cache: SkipList<Maybe<T>> = new SkipList<Maybe<T>>();

  private readonly resolution: TimeSpan;

  private readonly s3Credentials: I_S3Credentials;

  private readonly fetchQueue = createDispatchQueue(6);

  private readonly fetching: Set<number> = new Set<number>();

  constructor(
    fetch: (
      t: UnixTime,
      s3Credentials: I_S3Credentials
    ) => Promise<FetchResult<T>>,
    resolution: TimeSpan,
    s3Credentials: I_S3Credentials
  ) {
    this._fetch = fetch;
    this.resolution = resolution;
    this.s3Credentials = s3Credentials;
    this.gotData = new Subject<UnixTime>();
  }

  public prefetch(times: Array<UnixTime>, clear = true) {
    if (clear) {
      this.fetching.clear();
      this.fetchQueue.clear();
    }

    const timesWithPriority = times.map((time, index) => ({
      time,
      priority: reverseBits(index)
    }));
    timesWithPriority.sort((x, y) => x.priority - y.priority);

    for (let i = 0; i < timesWithPriority.length; i++) {
      const time = timesWithPriority[i].time.round(this.resolution);
      const t = time.ticks;

      const node = this.cache.find(t);
      if (node.index !== t) this.fetchData(time, this.s3Credentials);
    }
  }

  public get(timeStamp: UnixTime, fetch = true): Maybe<T> {
    const time = timeStamp.round(this.resolution);
    const t = time.ticks;

    const node = this.cache.find(t);
    if (node.index === t) return node.value;

    if (fetch) this.fetchData(time, this.s3Credentials);

    return this.interpolate(node, t);
  }

  public getSeries(sampleTimes: UnixTime[]): RecordSeries {
    this.prefetch(sampleTimes);
    return sampleTimes.map((time) => ({ time, value: this.get(time, false) }));
  }

  private interpolate(before: SkipListNode<Maybe<T>>, t: number): Maybe<T> {
    const dataBefore = before.value;
    const after = before.next[0];
    const dataAfter = after.value;

    if (isUndefined(dataBefore) && isUndefined(dataAfter)) return undefined;

    if (isUndefined(dataBefore)) return dataAfter;

    if (isUndefined(dataAfter)) return dataBefore;

    const p = t - before.index;
    const n = after.index - t;
    const pn = p + n;

    let interpolated: Record<string, I_CsvEntry> = {};

    //What about string nodes? like Alarms
    for (const k of Object.keys(dataBefore)) {
      const valueBefore = dataBefore[k].value;
      const valueAfter = dataAfter[k]?.value as Maybe<number | string>;

      let value: number | string;

      if (isUndefined(valueAfter)) {
        value = valueBefore;
      } else if (isNumber(valueBefore) && isNumber(valueAfter)) {
        value = (valueBefore * n + valueAfter * p) / pn;
      } else {
        value = n < p ? valueAfter : valueBefore;
      }

      interpolated[k] = { value, unit: dataBefore[k].unit };
    }

    return interpolated as T;
  }

  private fetchData(time: UnixTime, s3Credentials: I_S3Credentials) {
    const t = time.ticks;

    if (this.fetching.has(t))
      // we are already fetching t
      return;

    const fetchTask = () => {
      const onSuccess = (data: FetchResult<T>) => {
        if (data === FetchResult.tryLater) {
          console.warn(FetchResult.tryLater);
        }

        const value =
          data === FetchResult.notAvailable || data === FetchResult.tryLater
            ? undefined
            : data;
        // @ts-ignore
        this.cache.insert(value, t);
      };

      const onFailure = (_: unknown) => {
        console.error(time.ticks + ' FAILED!'); // should not happen
      };

      const dispatch = () => {
        this.fetching.delete(time.ticks);
        (this.gotData as Subject<UnixTime>).next(time);
      };

      return this._fetch(time, s3Credentials)
        .then(
          (d) => onSuccess(d),
          (f) => onFailure(f)
        )
        .finally(() => dispatch());
    };

    this.fetching.add(t);
    this.fetchQueue.dispatch(() => fetchTask());
  }
}
